/**
 * Verify Magic Link Page
 * Automatically verifies magic link code and authenticates user
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { verifyMagicLink } from "@/store/slices/authSlice";
import { getAndClearRedirectUrl } from "@/utils/storage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle, AlertTriangle } from "lucide-react";
import logo from "@/assets/logo.png";
import { logger } from "@/utils/logger";

export default function VerifyMagicLink() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { magicLinkStatus, magicLinkError } = useAppSelector(
    (state) => state.auth
  );
  const [hasAttempted, setHasAttempted] = useState(false);

  useEffect(() => {
    if (code && !hasAttempted) {
      handleVerification(code);
      setHasAttempted(true);
    }
  }, [code, hasAttempted]);

  const handleVerification = async (linkCode: string) => {
    const result = await dispatch(verifyMagicLink({ code: linkCode }));

    if (verifyMagicLink.fulfilled.match(result)) {
      // Redirect after 2 seconds
      setTimeout(() => {
        // Get redirect URL from localStorage (set by ProtectedRoute before redirecting to login)
        const redirectUrl = getAndClearRedirectUrl();
        logger.log(
          "[VerifyMagicLink] Redirecting after magic link verification:",
          { redirectUrl }
        );
        const from = redirectUrl || "/meeting-simulator";
        navigate(from, { replace: true });
      }, 2000);
    }
  };

  const handleRequestNew = () => {
    navigate("/login");
  };

  // VERIFYING STATE
  if (
    magicLinkStatus === "verifying" ||
    magicLinkStatus === "idle" ||
    !hasAttempted
  ) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col items-center text-center space-y-6">
              <img src={logo} alt="Logo" className="h-12 w-auto" />

              <div className="space-y-2">
                <div className="flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-foreground">
                  Verifying your magic link...
                </h1>
                <p className="text-muted-foreground">Please wait a moment</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // SUCCESS STATE
  if (magicLinkStatus === "verify-success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col items-center text-center space-y-6">
              <img src={logo} alt="Logo" className="h-12 w-auto" />

              <div className="space-y-2">
                <div className="flex items-center justify-center">
                  <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
                </div>
                <h1 className="text-2xl font-bold text-foreground">
                  Welcome back!
                </h1>
                <p className="text-muted-foreground">
                  Redirecting to your dashboard...
                </p>
              </div>

              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                <div className="bg-primary h-full animate-[progress_2s_ease-in-out]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ERROR STATE
  if (magicLinkStatus === "verify-error") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col items-center text-center space-y-6">
              <img src={logo} alt="Logo" className="h-12 w-auto" />

              <div className="space-y-2">
                <div className="flex items-center justify-center">
                  <AlertTriangle className="h-12 w-12 text-orange-600 dark:text-orange-400" />
                </div>
                <h1 className="text-2xl font-bold text-foreground">
                  This link is no longer valid
                </h1>
                <p className="text-sm text-muted-foreground max-w-sm">
                  {magicLinkError}
                </p>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Your magic link has expired or has already been used. Magic
                  links are valid for 10 minutes and can only be used once.
                </p>
              </div>

              <div className="w-full space-y-3">
                <Button onClick={handleRequestNew} className="w-full">
                  Request New Magic Link
                </Button>
                <Button
                  onClick={() => navigate("/login")}
                  variant="outline"
                  className="w-full"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
